/**
 * Neuigkeiten — ein einmaliger Dialog nach dem Anmelden, wenn eine Fassung
 * etwas mitbringt, das man sonst übersieht (HM-170, ADR 0048). Aufbau nach
 * `ui_kits/dashboard/Neuigkeiten.html`.
 *
 * **Ein Eintrag je Anlass, und gezeigt wird nur der neueste.** Ein Archiv gibt
 * es nicht: Wer zwei Fassungen lang nicht da war, sieht die jüngsten
 * Neuigkeiten und nicht eine Schlange alter Dialoge. Ob gesehen, steht **am
 * Konto** (`CurrentUser.newsSeen`) und nicht im Browser — „einmal" heißt einmal
 * je Mensch, nicht einmal je Gerät.
 *
 * **Wer nach dem Anlass dazukam, bekommt ihn nicht.** Für ein Konto, das nach
 * `since` angelegt wurde, ist nichts davon neu; es sähe sonst als Erstes einen
 * Dialog über eine Funktion, die für es schon immer da war.
 */
import { timestampMs } from "./format.js";

export interface NewsItem {
  readonly id: string;
  readonly title: string;
  readonly text: string;
  /** Ein GIF unter `public/neuigkeiten/`, 16:10, ohne fremden Server (CSP `img-src 'self'`). */
  readonly gif: string;
  /** Beschreibung des GIFs für Sprachausgaben. */
  readonly gifAlt: string;
  /** Ein Weg zur Funktion, falls es einen gibt — der Rahmen weiß, was er bedeutet. */
  readonly action?: { readonly label: string; readonly target: NewsTarget };
}

/** Wohin ein Eintrag führt. Ein Dialog hat keine Adresse und steht deshalb als Schlüssel hier. */
export type NewsTarget = "send-feedback";

export interface NewsEntry {
  /** Die Kennung am Konto — sie darf sich nach dem Ausrollen nicht ändern. */
  readonly id: string;
  /** Die Fassung, die es mitbrachte — steht im Kopf („Neu in 0.66.0"). */
  readonly version: string;
  /** Ab wann es neu ist; ein später angelegtes Konto bekommt den Eintrag nicht. */
  readonly since: string;
  readonly items: readonly NewsItem[];
}

/** Neueste zuerst. Wer einen Eintrag hinzufügt, setzt ihn an den Anfang. */
export const NEWS: readonly NewsEntry[] = [
  {
    id: "2026-10-feedback",
    version: "0.66.0",
    /* Der Zeitpunkt des Ausrollens von 0.66.2 — wer bis dahin ein Konto hat, auch vom
       13.10., sieht ihn einmal; wer danach dazukommt, nicht (Auftraggeber, 13.10.2026). */
    since: "2026-10-13T04:45:00Z",
    items: [
      {
        id: "feedback",
        title: "Fehler melden und Wünsche äußern",
        text:
          "Unten links im Kontomenü steht jetzt „Feedback senden“. Beschreib, was nicht stimmt oder was dir fehlt — " +
          "die Fassung der App, dein Browser und die Seite, auf der du gerade bist, gehen mit, damit wir es nachvollziehen können. " +
          "Unter „Wird mitgesendet“ siehst du vor dem Senden, was beigelegt wird.",
        gif: "/neuigkeiten/feedback-senden.gif",
        gifAlt: "Kontomenü öffnen, „Feedback senden“ wählen, Titel und Beschreibung eingeben und senden.",
        action: { label: "Feedback senden", target: "send-feedback" },
      },
    ],
  },
];

/**
 * Die Neuigkeiten, die dieser Nutzer jetzt sehen soll — oder `null`.
 *
 * Nur der neueste Eintrag kommt in Frage; ist er gesehen oder älter als das
 * Konto, gibt es nichts.
 */
export function dueNews(
  user: { readonly newsSeen: string | null; readonly createdAt: string },
  entries: readonly NewsEntry[] = NEWS,
): NewsEntry | null {
  const latest = entries[0];
  if (latest === undefined || latest.items.length === 0) return null;
  if (user.newsSeen === latest.id) return null;
  const created = timestampMs(user.createdAt);
  const since = timestampMs(latest.since);
  // Nicht zu deuten heißt: nicht anzeigen — ein Dialog aus einer Vermutung ist keiner.
  if (created === null || since === null || created >= since) return null;
  return latest;
}
